import type { SelectPaginatedData } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface PaymentPoliciesOptionsApiOptions {
  query?: string
  page?: number
  signal?: AbortSignal
  /** Force the backend to include these option ids in the response, even if
   *  pagination would normally exclude them (off-page case). Sent as
   *  `preselected[0]=<id>&preselected[1]=<id>...` query params. Use sparingly
   *  — only for ids the user has saved that must appear in the dropdown. */
  preselected?: number[]
}

export const usePaymentPoliciesOptionsApi = async (
  opts: PaymentPoliciesOptionsApiOptions = {},
): Promise<SelectPaginatedData | null> => {
  const { query = '', page = 1, signal, preselected } = opts
  const { $axios } = useNuxtApp()

  const toast = useToast()

  const url = useAdminRoutes('admin.payment-policies.list')

  if (!url) {
    toast.error('Unexpected error. Invalid request URL.')
    return null
  }

  try {
    const params: Record<string, string | number> = { page }
    if (query !== '') params.query = query
    if (preselected !== undefined) {
      preselected.forEach((id, idx) => {
        params[`preselected[${idx}]`] = id
      })
    }

    const { data } = await $axios.get(url, { params, signal })
    const paginatedData: SelectPaginatedData | null = data?.data || null

    if (!data.success || !paginatedData) {
      toast.error('Unexpected error.')
      return null
    }

    return paginatedData
  }
  catch (err: unknown) {
    if ((err as Error)?.name === 'CanceledError') return null
    toast.error(getAxiosErrorMessage(err))
    return null
  }
}
